// src/components/Pricing.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom'; // Ensure Link is imported
import '../styles/App.css'; // Import the consolidated App.css
import '../styles/Pricing.css';
import { FaUser, FaStar, FaShieldAlt, FaCheck } from 'react-icons/fa'; // Import FaCheck for feature checkmarks

function Pricing() {
  return (
    <main className="pricing-page">
      <Helmet>
        <title>Pricing Plans | WizeAssist</title>
        <meta
          name="description"
          content="Choose the perfect WizeAssist plan tailored to your needs. Contact us to get a customized price or sign up for a demo to experience our AI-driven sports training solutions."
        />
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Pricing Plans | WizeAssist" />
        <meta
          property="og:description"
          content="Choose the perfect WizeAssist plan tailored to your needs. Contact us to get a customized price or sign up for a demo to experience our AI-driven sports training solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.wizeassist.com/pricing" />
        <meta
          property="og:image"
          content="https://www.wizeassist.com/assets/images/og-image-pricing.jpg"
        /> {/* Replace with actual image */}

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing Plans | WizeAssist" />
        <meta
          name="twitter:description"
          content="Choose the perfect WizeAssist plan tailored to your needs. Contact us to get a customized price or sign up for a demo to experience our AI-driven sports training solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.wizeassist.com/assets/images/twitter-image-pricing.jpg"
        />
        {/* Structured Data for Pricing Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "WizeAssist AI-Driven Sports Training",
            "description":
              "WizeAssist offers tailored AI-driven sports training solutions for individuals and teams to enhance performance, prevent injuries, and achieve athletic excellence.",
            "provider": {
              "@type": "Organization",
              "name": "WizeAssist",
              "url": "https://www.wizeassist.com",
              "logo": "https://www.wizeassist.com/assets/images/logo.png", // Replace with actual logo URL
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "Basic",
                "description":
                  "Perfect for beginners. Includes real-time feedback, basic analytics, and a pinch of neon magic.",
                "price": "Contact Us for Pricing",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": "https://www.wizeassist.com/contact",
              },
              {
                "@type": "Offer",
                "name": "Pro",
                "description":
                  "Go deeper with advanced analytics, priority support, and exclusive gear deals.",
                "price": "Contact Us for Pricing",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": "https://www.wizeassist.com/contact",
              },
              {
                "@type": "Offer",
                "name": "Elite",
                "description":
                  "Teams, clinics, or serious grinders get the entire suite—unlimited features, AI expansions, and 24/7 support.",
                "price": "Contact Us for Pricing",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": "https://www.wizeassist.com/contact",
              },
            ],
          })}
        </script>
      </Helmet>

      <h1 className="pricing-page-title">Power Up Your Membership</h1>

      <div className="pricing-cards container">
        {/* Basic Plan */}
        <div className="pricing-card glass p-4 d-flex flex-column">
          <div className="pricing-card-header mb-3 position-relative">
            <FaUser className="pricing-icon text-teal mb-2" size={40} />
            <h2 className="text-teal">Basic</h2>
          </div>
          <p className="pricing-description">
            Perfect for beginners. Includes real-time feedback, basic analytics, and a pinch of neon magic.
          </p>
          <ul className="pricing-features">
            <li><FaCheck className="text-teal me-2" /> Real-time feedback</li>
            <li><FaCheck className="text-teal me-2" /> Basic analytics</li>
            <li><FaCheck className="text-teal me-2" /> Neon magic integrations</li>
          </ul>
          <div className="mt-auto">
            <Link to="/contact" className="btn btn-secondary w-100" aria-label="Contact Us for Basic Plan">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Pro Plan */}
        <div className="pricing-card most-popular glass p-4 d-flex flex-column">
          <div className="pricing-card-header mb-3 position-relative">
            <FaStar className="pricing-icon text-purple mb-2" size={40} />
            <h2 className="text-purple">Pro</h2>
            <span className="badge bg-accent text-dark position-absolute top-0 end-0 mt-2 me-2">Most Popular</span>
          </div>
          <p className="pricing-description">
            Go deeper with advanced analytics, priority support, and exclusive gear deals.
          </p>
          <ul className="pricing-features">
            <li><FaCheck className="text-teal me-2" /> Advanced analytics</li>
            <li><FaCheck className="text-teal me-2" /> Priority support</li>
            <li><FaCheck className="text-teal me-2" /> Exclusive gear deals</li>
          </ul>
          <div className="mt-auto">
            <Link to="/contact" className="btn btn-primary w-100" aria-label="Contact Us for Pro Plan">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Elite Plan */}
        <div className="pricing-card glass p-4 d-flex flex-column">
          <div className="pricing-card-header mb-3 position-relative">
            <FaShieldAlt className="pricing-icon text-green mb-2" size={40} />
            <h2 className="text-green">Elite</h2>
          </div>
          <p className="pricing-description">
            Teams, clinics, or serious grinders get the entire suite—unlimited features, AI expansions, and 24/7 support.
          </p>
          <ul className="pricing-features">
            <li><FaCheck className="text-teal me-2" /> Unlimited features</li>
            <li><FaCheck className="text-teal me-2" /> AI expansions</li>
            <li><FaCheck className="text-teal me-2" /> 24/7 support</li>
          </ul>
          <div className="mt-auto">
            <Link to="/contact" className="btn btn-secondary w-100" aria-label="Contact Us for Elite Plan">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Pricing;
